// src/services/cacheService.js
import { API_BASE_URL } from '../API/api';

class CacheService {
  constructor() {
    this.cachePrefix = 'rdv_cache_';
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.refreshThreshold = 2 * 60 * 1000; // 2 minutes before expiry to refresh
    this.maxCacheSize = 50 * 1024 * 1024; // 50MB max cache size
    this.refreshQueue = new Set();
    this.isRefreshing = false;
    
    // Initialize cache cleanup
    this.initializeCache();
  }

  // Generate cache key from endpoint and parameters
  generateCacheKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${this.cachePrefix}${endpoint}_${JSON.stringify(sortedParams)}`;
  }

  // Check if cache entry is valid
  isCacheValid(entry) {
    if (!entry || !entry.timestamp) return false;
    const now = Date.now();
    return (now - entry.timestamp) < entry.ttl;
  }

  // Check if cache entry needs refresh
  needsRefresh(entry) {
    if (!entry || !entry.timestamp) return true;
    const now = Date.now();
    return (now - entry.timestamp) > (entry.ttl - this.refreshThreshold);
  }

  // Get cached data
  get(endpoint, params = {}) {
    try {
      const key = this.generateCacheKey(endpoint, params);
      const cached = sessionStorage.getItem(key);
      
      if (!cached) return null;
      
      const entry = JSON.parse(cached);
      
      if (!this.isCacheValid(entry)) {
        // Cache expired, remove it
        sessionStorage.removeItem(key);
        return null;
      }
      
      // Check if needs background refresh
      if (this.needsRefresh(entry) && !this.refreshQueue.has(key)) {
        this.scheduleBackgroundRefresh(endpoint, params, key);
      }
      
      return entry.data;
    } catch (error) {
      console.error('Cache get error:', error);
      return null;
    }
  }

  // Set cached data
  set(endpoint, params = {}, data, ttl = this.defaultTTL) {
    try {
      const key = this.generateCacheKey(endpoint, params);
      const entry = {
        data,
        timestamp: Date.now(),
        ttl,
        endpoint,
        params
      };
      
      // Check cache size before storing
      if (this.getCacheSize() + JSON.stringify(entry).length > this.maxCacheSize) {
        this.cleanupOldEntries();
      }
      
      sessionStorage.setItem(key, JSON.stringify(entry));
      return true;
    } catch (error) {
      console.error('Cache set error:', error);
      return false;
    }
  }

  // Schedule background refresh
  scheduleBackgroundRefresh(endpoint, params, key) {
    if (this.refreshQueue.has(key)) return;
    
    this.refreshQueue.add(key);
    
    // Use setTimeout to avoid blocking the UI
    setTimeout(() => {
      this.performBackgroundRefresh(endpoint, params, key);
    }, 100);
  }

  // Perform background refresh
  async performBackgroundRefresh(endpoint, params, key) {
    try {
      if (this.isRefreshing) return;
      
      this.isRefreshing = true;
      
      // Using relative URLs to go through CloudFront distribution
      
      let response;
      switch (endpoint) {
        case 'projects':
          response = await fetch(`${API_BASE_URL}/api/projects`);
          break;
        case 'projectsSearch':
          response = await fetch(`${API_BASE_URL}/api/projectsSearch?${new URLSearchParams(params).toString()}`);
          break;
        case 'new_project_id':
          response = await fetch(`${API_BASE_URL}/api/new_project_id?${new URLSearchParams(params).toString()}`);
          break;
        default:
          console.warn(`Unknown endpoint for background refresh: ${endpoint}`);
          return;
      }
      
      if (response.ok) {
        const data = await response.json();
        this.set(endpoint, params, data);
        console.log(`Background refresh completed for ${endpoint}`);
      }
    } catch (error) {
      console.error(`Background refresh failed for ${endpoint}:`, error);
    } finally {
      this.refreshQueue.delete(key);
      this.isRefreshing = false;
    }
  }

  // Force refresh specific cache entry
  async forceRefresh(endpoint, params = {}) {
    try {
      // Using relative URLs to go through CloudFront distribution
      
      let response;
      switch (endpoint) {
        case 'projects':
          response = await fetch(`${API_BASE_URL}/api/projects`);
          break;
        case 'projectsSearch':
          response = await fetch(`${API_BASE_URL}/api/projectsSearch?${new URLSearchParams(params).toString()}`);
          break;
        case 'new_project_id':
          response = await fetch(`${API_BASE_URL}/api/new_project_id?${new URLSearchParams(params).toString()}`);
          break;
        default:
          throw new Error(`Unknown endpoint: ${endpoint}`);
      }
      
      if (response.ok) {
        const data = await response.json();
        this.set(endpoint, params, data);
        return data;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Force refresh failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Clear specific cache entry
  clear(endpoint, params = {}) {
    try {
      const key = this.generateCacheKey(endpoint, params);
      sessionStorage.removeItem(key);
      this.refreshQueue.delete(key);
      return true;
    } catch (error) {
      console.error('Cache clear error:', error);
      return false;
    }
  }

  // Clear all cache
  clearAll() {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          sessionStorage.removeItem(key);
        }
      });
      this.refreshQueue.clear();
      return true;
    } catch (error) {
      console.error('Cache clear all error:', error);
      return false;
    }
  }

  // Get cache size in bytes
  getCacheSize() {
    try {
      let totalSize = 0;
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          totalSize += sessionStorage.getItem(key).length;
        }
      });
      return totalSize;
    } catch (error) {
      console.error('Cache size calculation error:', error);
      return 0;
    }
  }

  // Cleanup old entries when cache is full
  cleanupOldEntries() {
    try {
      const entries = [];
      const keys = Object.keys(sessionStorage);
      
      // Collect all cache entries with their timestamps
      keys.forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          try {
            const entry = JSON.parse(sessionStorage.getItem(key));
            entries.push({ key, timestamp: entry.timestamp, size: sessionStorage.getItem(key).length });
          } catch (e) {
            // Remove corrupted entries
            sessionStorage.removeItem(key);
          }
        }
      });
      
      // Sort by timestamp (oldest first)
      entries.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest entries until we have space
      let removedSize = 0;
      const targetRemoval = this.maxCacheSize * 0.3; // Remove 30% of max size
      
      for (const entry of entries) {
        if (removedSize >= targetRemoval) break;
        
        sessionStorage.removeItem(entry.key);
        this.refreshQueue.delete(entry.key);
        removedSize += entry.size;
      }
      
      console.log(`Cache cleanup: removed ${removedSize} bytes`);
    } catch (error) {
      console.error('Cache cleanup error:', error);
    }
  }

  // Initialize cache and cleanup
  initializeCache() {
    try {
      // Clean up expired entries on startup
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          try {
            const entry = JSON.parse(sessionStorage.getItem(key));
            if (!this.isCacheValid(entry)) {
              sessionStorage.removeItem(key);
            }
          } catch (e) {
            // Remove corrupted entries
            sessionStorage.removeItem(key);
          }
        }
      });
      
      console.log('Cache service initialized with sessionStorage');
    } catch (error) {
      console.error('Cache initialization error:', error);
    }
  }

  // Get cache statistics
  getStats() {
    try {
      const keys = Object.keys(sessionStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.cachePrefix));
      
      let totalSize = 0;
      let validEntries = 0;
      let expiredEntries = 0;
      
      cacheKeys.forEach(key => {
        try {
          const entry = JSON.parse(sessionStorage.getItem(key));
          totalSize += sessionStorage.getItem(key).length;
          
          if (this.isCacheValid(entry)) {
            validEntries++;
          } else {
            expiredEntries++;
          }
        } catch (e) {
          // Skip corrupted entries
        }
      });
      
      return {
        totalEntries: cacheKeys.length,
        validEntries,
        expiredEntries,
        totalSize,
        maxSize: this.maxCacheSize,
        refreshQueueSize: this.refreshQueue.size
      };
    } catch (error) {
      console.error('Cache stats error:', error);
      return null;
    }
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;
